import { useState } from 'react';
import { Box, Card, CardContent, Link } from '@mui/material';
import { AttachmentFile } from '@components/container/AttachmentFile';
import { Artifact, PostArtifact } from '@models/note';
import { TooltipDownload, TooltipRemove, TooltipAdditionalActions, Tooltip } from '@components/tooltip';
import { IMenuItem } from '@components/menu';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { CardHeaderHoverable } from '@components/noteMessage/CardHeaderHoverable';
import { IconButton } from '@components/iconButton';
import { FourArrowExpand } from '@assets/svgs';
import { DocumentDialog } from '@components/dialogs/DocumentDialog';

const headerStyle = {
  '&.MuiCardHeader-root': {
    display: 'flex',
    padding: 0,
    justifyContent: 'space-between',
  },
  '& .MuiCardHeader-content': {
    display: 'flex',
    overflow: 'hidden',
    flex: 'none',
  },
  '& .MuiCardHeader-action': {
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    padding: 0,
    margin: '0px',
    bottom: '-8px',
    right: '14px',
    flex: 'none',
  },
} as const;

interface ICardAttachmentFile {
  state: 'added' | 'sent';
  isNote: boolean;
  searchBarText?: string;
  onDisabledHoverParent?: () => void;
  onActiveHoverParent?: () => void;
  handleRemoveFile?: () => void;
  menuItems: IMenuItem[];
  variant?: 'regular' | 'dense';
  allowViewInModal?: boolean;
}

export const CardAttachmentFile = (props: (Artifact | PostArtifact) & ICardAttachmentFile) => {
  const {
    allowViewInModal: allowViewInModalBase,
    state,
    searchBarText,
    onDisabledHoverParent,
    onActiveHoverParent,
    description,
    handleRemoveFile,
    menuItems,
  } = props;

  const fileType = (props as any)?.source?.dmsId?.split('.').slice(-1)[0]?.toLowerCase();
  const fileExtension = (props as any)?.fileName?.split('.').slice(-1)[0]?.toLowerCase();

  const allowViewInModal =
    allowViewInModalBase &&
    (['pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg'].includes(fileType) ||
      ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg'].includes(fileExtension));

  const [onHover, setHover] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState<boolean>(false);

  const downloadUrl = 'source' in props ? props.source.downloadUrl : undefined;

  const handleOnHover = () => {
    setHover(true);
    if (typeof onDisabledHoverParent === 'function') {
      onDisabledHoverParent();
    }
  };

  const handleOutHover = () => {
    setHover(false);
    if (typeof onActiveHoverParent === 'function') {
      onActiveHoverParent();
    }
  };
  return (
    <Box
      sx={{
        maxWidth: '100%',
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        marginTop: '2px',
      }}
    >
      <Card
        elevation={props?.variant === 'dense' ? 4 : 3}
        onMouseEnter={handleOnHover}
        onMouseLeave={handleOutHover}
        onFocus={handleOnHover}
        onBlur={handleOutHover}
        tabIndex={0}
        sx={{
          ...(props?.variant === 'dense' && {
            boxShadow: 'none',
            backgroundColor: 'background.main',
            borderRadius: 10,

            // intentionally override value in CardHeaderHoverable
            ' .MuiCardHeader-action': {
              right: '-1px !important',
              top: '10px',
            },
          }),
        }}
      >
        {state === 'added' ? (
          <>
            <CardHeaderHoverable>
              {onHover ? (
                <Box sx={{ display: 'flex' }}>
                  {allowViewInModal && (
                    <Tooltip title='View In Modal'>
                      <IconButton
                        variant='rounded'
                        size='small'
                        sx={{ color: 'text.main' }}
                        onClick={() => setIsDocumentViewerOpen(true)}
                      >
                        <FourArrowExpand width='15px' height='15px' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      marginTop: '16px',
                    }}
                  >
                    <TooltipRemove onClick={handleRemoveFile} />
                    <TooltipAdditionalActions items={menuItems} onCloseMenu={handleOutHover} />
                  </Box>
                </Box>
              ) : null}
            </CardHeaderHoverable>
            <CardContent>
              <AttachmentFile searchBarText={searchBarText} {...props} />
            </CardContent>
          </>
        ) : (
          <>
            <CardHeaderHoverable sx={headerStyle}>
              {onHover ? (
                <Box sx={{ display: 'flex' }}>
                  {allowViewInModal && (
                    <Tooltip title='View In Modal'>
                      <IconButton
                        variant='rounded'
                        size='small'
                        sx={{ color: 'text.main' }}
                        onClick={() => setIsDocumentViewerOpen(true)}
                      >
                        <FourArrowExpand width='15px' height='15px' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Link href={downloadUrl} underline='none' target='_blank' rel='noopener'>
                    <TooltipDownload />
                  </Link>
                </Box>
              ) : null}
            </CardHeaderHoverable>
            <CardContent>
              <AttachmentFile searchBarText={searchBarText} {...props} />
            </CardContent>
          </>
        )}
      </Card>
      {description && props?.variant !== 'dense' ? (
        <TypographyHighlighter text={description} highlightText={searchBarText} variant='caption' marginTop='4px' />
      ) : null}

      <DocumentDialog
        open={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        title={`View Document: ${'fileName' in props && props.fileName ? props.fileName : ''}`}
        downloadUrl={downloadUrl || undefined}
      />
    </Box>
  );
};
